import { useState, useRef, useEffect, useCallback } from "react";
import {
  Shield,
  Crosshair,
  Newspaper,
  Gavel,
  Users,
  Megaphone,
  Award,
  DollarSign,
  GraduationCap,
  FileSearch,
  Building2,
  Globe2,
  Radar,
} from "lucide-react";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import ArticleCard from "../../Universal/ArticleCard";
import { useArticleSidebar } from "../../Universal/ArticleCardDetailPanel";
import { useAptData } from "../../../hooks/useAptData";
import { getAptPosts } from "../../../api/apts";
import { toggleThumb } from "../../../api/feed";
import makeid from "../../Universal/Makeid";
import { useAptSearch } from "../../../hooks/useAptSearch";
import { getCountryFlag } from "../../../utils/flags";
import { IconPill } from "../../Universal/IconPill";
import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from "@heroicons/react/24/outline";

const PREVIEW_COUNT = 6;
const OPS_PREVIEW = 8;

const useAptPosts = (actorName) => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!actorName) {
      setPosts([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    getAptPosts(actorName)
      .then((res) => setPosts(res.data.posts ?? []))
      .catch((e) => setError(e))
      .finally(() => setIsLoading(false));
  }, [actorName]);

  return { posts, isLoading, error };
};

/* ─── Content type badge config ─── */
const TYPE_BADGES = {
  court_cases: { icon: Gavel, color: "#f59e0b", label: "Court Case" },
  neighbors: { icon: Users, color: "#8b5cf6", label: "Neighbors" },
  press_release: { icon: Megaphone, color: "#10b981", label: "Press Release" },
  gov_award: { icon: Award, color: "#f97316", label: "Gov Award" },
  financial_crawl: { icon: DollarSign, color: "#06b6d4", label: "Financial" },
  edu_crawl: { icon: GraduationCap, color: "#ec4899", label: "Education" },
  investigative_doc: {
    icon: FileSearch,
    color: "#ef4444",
    label: "Investigative",
  },
  company_subsidiary: {
    icon: Building2,
    color: "#6366f1",
    label: "Subsidiary",
  },
  domain_crawl: { icon: Globe2, color: "#14b8a6", label: "Domain Crawl" },
  hunt: { icon: Radar, color: "#0ea5e9", label: "Hunt" },
  apt_news: { icon: Newspaper, color: "#e11d48", label: "APT News" },
};

const TypeBadge = ({ type }) => {
  const badge = TYPE_BADGES[type];
  if (!badge) return null;
  const Icon = badge.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[10px] font-normal whitespace-nowrap bg-cardInnerBg"
      style={{ color: badge.color, border: `1px solid ${badge.color}33` }}
    >
      <Icon size={10} style={{ flexShrink: 0 }} />
      {badge.label}
    </span>
  );
};

const TargetBadge = ({ target }) => {
  if (!target) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[10px] font-normal whitespace-nowrap bg-cardInnerBg text-velBlue"
      style={{
        border: "1px solid rgb(var(--c-velBlue) / 0.3)",
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <Crosshair size={10} style={{ flexShrink: 0 }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {target}
      </span>
    </span>
  );
};

const AptBadge = ({ aptName }) => {
  if (!aptName) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg text-[10px] font-normal whitespace-nowrap bg-cardInnerBg"
      style={{
        color: "#f472b6",
        border: "1px solid rgba(244,114,182,0.3)",
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <Shield size={10} style={{ flexShrink: 0 }} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {aptName}
      </span>
    </span>
  );
};

/* ─── Card info overlay ─── */
const CardPreview = ({ article }) => {
  if (!article) return null;
  const title = article.title || article.url || "Untitled";
  const source =
    article.source ||
    (article.url
      ? (() => {
          try {
            return new URL(article.url).hostname.replace("www.", "");
          } catch {
            return "";
          }
        })()
      : "");
  const snippet = article.content
    ? article.content.length > 180
      ? article.content.slice(0, 180).trim() + "…"
      : article.content
    : "";
  const type = article.target_type || article.type;
  const target = article.target || null;
  const aptName = article.apt_name || null;

  return (
    <div className="px-3 py-2.5 bg-cardInnerBg border-b border-mainDivider pointer-events-none">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {type && <TypeBadge type={type} />}
        {target && <TargetBadge target={target} />}
        {aptName && <AptBadge aptName={aptName} />}
        {source && (
          <span className="text-[10px] truncate text-mutedText">{source}</span>
        )}
      </div>
      <p className="text-xs font-normal leading-snug line-clamp-2 mb-1 text-mainText">
        {title}
      </p>
      {snippet && (
        <p className="text-[11px] leading-relaxed line-clamp-3 text-mutedText">
          {snippet}
        </p>
      )}
    </div>
  );
};

/* ─── FeedCard wrapper ─── */
const AptFeedCard = ({
  id,
  article,
  onCardClick,
  isActive,
  onCardInteract,
}) => {
  const [broken, setBroken] = useState(false);
  if (broken) return null;

  const typeBadge = (
    <>
      <TypeBadge type={article.target_type || article.type} />
      {article.target && <TargetBadge target={article.target} />}
      {article.apt_name && <AptBadge aptName={article.apt_name} />}
    </>
  );

  return (
    <div
      className="relative cursor-pointer"
      onClick={() => onCardClick(id, article)}
    >
      <ArticleCard
        investigateDoc={(doc) => onCardClick(id, doc)}
        article={article}
        articleURL={article.url}
        articleLike={article.like}
        articleDislike={article.dislike}
        articleCatalog={article.catalog}
        lid={`${id}||like`}
        dlid={`${id}||dislike`}
        cid={`${id}||catalog`}
        isActive={isActive}
        typeBadge={typeBadge}
        hoverPreview={<CardPreview article={article} />}
        onInteract={(state) => onCardInteract?.(id, article.document_id, state)}
        onBroken={() => setBroken(true)}
      />
    </div>
  );
};

/* ─── Panel ─── */
const Panel = ({ children, className = "" }) => (
  <div
    className={`relative overflow-hidden rounded-lg border border-surfaceBorder bg-surface px-6 py-5 ${className}`}
  >
    <div className="relative z-10">{children}</div>
  </div>
);

const tagClasses =
  "inline-flex items-center px-3 py-1 rounded-lg text-xs tracking-wide bg-cardInnerBg text-mutedText border border-surfaceBorder";

const toolTagClasses =
  "inline-flex items-center px-3 py-1 rounded-lg text-xs tracking-wide bg-velBlue/[0.07] text-velBlue border border-velBlue/15";

const expandBtnClasses =
  "self-center px-4 py-1.5 my-2 text-xs tracking-wide rounded-lg transition border border-btnBorder text-mutedText hover:text-velBlue hover:border-velBlue/30 cursor-pointer";

/* ─── Back / nav button ─── */
const BackButton = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs tracking-wide text-mutedText border border-btnBorder hover:text-velBlue hover:border-velBlue/30 transition self-start cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
  >
    <svg
      className="h-4 w-4"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 3L5 8l5 5" />
    </svg>
    <span>{children}</span>
  </button>
);

const ForwardButton = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs tracking-wide text-velBlue border border-velBlue/20 hover:bg-velBlue/[0.06] transition self-start cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
  >
    <span>{children}</span>
    <svg
      className="h-4 w-4"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3l5 5-5 5" />
    </svg>
  </button>
);

export const IndividualApt = ({ target, onBack }) => {
  const { aptData, posts, isLoading, error } = useAptData(target);

  return (
    <div className="relative min-h-screen w-full bg-mainBg font-poppins font-normal text-mainText pt-20 pb-20 px-4 md:px-8">
      <div className="max-w-5xl mx-auto flex flex-col">
        <BackButton onClick={onBack}>Back to all actors</BackButton>

        {isLoading && <LoadingSpinner />}

        {error && !isLoading && (
          <div className="flex items-center justify-center py-32">
            <p className="text-sm tracking-wide text-errorText">
              Failed to load threat actor data
            </p>
          </div>
        )}

        {!isLoading && !error && aptData && (
          <AptProfile aptData={aptData} posts={posts} />
        )}
      </div>
    </div>
  );
};

export const AptPostsView = ({ target, onBack, onViewProfile }) => {
  const {
    aptData,
    isLoading: aptLoading,
    error: aptError,
  } = useAptData(target);
  const aptName = aptData?.names?.length > 0 ? aptData.names[0].name : null;
  const {
    posts: aptPosts,
    isLoading: postsLoading,
    error: postsError,
  } = useAptPosts(aptName);
  const isLoading = aptLoading || postsLoading;
  const error = aptError || postsError;

  const [articleCards, setArticleCards] = useState([]);
  useEffect(() => {
    const sorted = [...aptPosts].sort((a, b) => {
      const da = a.date?.$date ? new Date(a.date.$date) : 0;
      const db = b.date?.$date ? new Date(b.date.$date) : 0;
      return db - da;
    });
    setArticleCards(sorted.map((post) => ({ id: makeid(12), article: post })));
  }, [aptPosts]);

  const handleInteract = useCallback((cardId, docId, state) => {
    setArticleCards((prev) =>
      prev.map((entry) =>
        entry.article.document_id === docId
          ? {
              ...entry,
              article: {
                ...entry.article,
                like: state.like,
                dislike: state.dislike,
                catalog: state.catalog,
              },
            }
          : entry,
      ),
    );
  }, []);

  const { activeCardId, handleCardClick } = useArticleSidebar(
    articleCards,
    handleInteract,
  );

  return (
    <div className="relative min-h-screen w-full bg-mainBg font-poppins font-normal text-mainText pt-20 pb-20 px-4 md:px-8">
      <div className="max-w-5xl mx-auto flex flex-col">
        <div className="flex items-center gap-3 mb-6">
          <BackButton onClick={onBack}>Back to all actors</BackButton>
          {onViewProfile && !isLoading && aptData && (
            <ForwardButton onClick={onViewProfile}>
              View full profile
            </ForwardButton>
          )}
        </div>

        {isLoading && <LoadingSpinner />}

        {error && !isLoading && (
          <div className="flex items-center justify-center py-32">
            <p className="text-sm tracking-wide text-errorText">
              Failed to load threat actor data
            </p>
          </div>
        )}

        {!isLoading && !error && aptData && (
          <>
            <Panel>
              {aptData.actor && (
                <h1 className="text-2xl md:text-3xl font-medium tracking-wide text-mainText leading-snug">
                  {aptData.actor}
                </h1>
              )}
              <div className="flex flex-wrap gap-x-8 gap-y-3 mt-4 pt-4 border-t border-mainDivider">
                {aptData.country?.length > 0 && (
                  <MetaItem label="Origin" value={aptData.country.join(", ")} />
                )}
                {aptData.sponsor && (
                  <MetaItem label="Sponsor" value={aptData.sponsor} />
                )}
                {(aptData.motivation || []).length > 0 && (
                  <MetaItem
                    label="Motivation"
                    value={(aptData.motivation || []).join(", ")}
                  />
                )}
                {aptData["first-seen"] && (
                  <MetaItem label="First Seen" value={aptData["first-seen"]} />
                )}
              </div>
            </Panel>

            <div className="mt-6">
              <h2 className="text-xs tracking-widest uppercase text-mutedText flex items-baseline flex-wrap gap-x-1 mb-4">
                Posts
                <span className="ml-2 text-mutedText font-normal">
                  {articleCards.length}{" "}
                  {articleCards.length === 1 ? "post" : "posts"}
                </span>
              </h2>

              {articleCards.length > 0 ? (
                <div className="grid gap-5 grid-cols-[repeat(auto-fill,_minmax(320px,_1fr))] justify-center">
                  {articleCards.map(({ id, article }) => (
                    <AptFeedCard
                      key={id}
                      id={id}
                      article={article}
                      onCardClick={handleCardClick}
                      onCardInteract={handleInteract}
                      isActive={activeCardId === id}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm tracking-wide text-mutedText mt-4">
                  No posts found for this actor.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AptProfile = ({ aptData, posts }) => {
  const [aliasesExpanded, setAliasesExpanded] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [countriesExpanded, setCountriesExpanded] = useState(false);
  const [opsExpanded, setOpsExpanded] = useState(false);
  const [counterOpsExpanded, setCounterOpsExpanded] = useState(false);

  if (!aptData) return null;

  const aliases = aptData.names || [];
  const ops = aptData.operations || [];
  const counterOps = aptData["counter-operations"] || [];
  const tools = aptData.tools || [];
  const sectors = aptData["observed-sectors"] || [];
  const countries = aptData["observed-countries"] || [];
  const motivation = aptData.motivation || [];
  const visibleOps = opsExpanded ? ops : ops.slice(-OPS_PREVIEW);
  const visibleCounterOps = counterOpsExpanded
    ? counterOps
    : counterOps.slice(-OPS_PREVIEW);

  return (
    <>
      <Panel>
        {aptData.actor && (
          <h1 className="text-2xl md:text-3xl font-medium tracking-wide text-mainText leading-snug">
            {aptData.actor}
          </h1>
        )}
        <div className="flex flex-wrap gap-x-8 gap-y-3 mt-4 pt-4 border-t border-mainDivider">
          {aptData.country?.length > 0 && (
            <MetaItem label="Origin" value={aptData.country.join(", ")} />
          )}
          {aptData.sponsor && (
            <MetaItem label="Sponsor" value={aptData.sponsor} />
          )}
          {motivation.length > 0 && (
            <MetaItem label="Motivation" value={motivation.join(", ")} />
          )}
          {aptData["first-seen"] && (
            <MetaItem label="First Seen" value={aptData["first-seen"]} />
          )}
        </div>
      </Panel>

      {aliases.length > 0 && (
        <Panel className="mt-3">
          <SectionLabel>
            Also Known As
            {aliases.length > PREVIEW_COUNT && (
              <span className="ml-2 text-mutedText font-normal">
                {aliases.length} names
              </span>
            )}
          </SectionLabel>
          <div className="flex flex-wrap gap-2 mt-3">
            {(aliasesExpanded ? aliases : aliases.slice(0, PREVIEW_COUNT)).map(
              (a) => (
                <AliasTag key={a.name} name={a.name} giver={a["name-giver"]} />
              ),
            )}
            {aliases.length > PREVIEW_COUNT && (
              <ExpandToggle
                expanded={aliasesExpanded}
                total={aliases.length}
                label="names"
                onToggle={() => setAliasesExpanded((v) => !v)}
              />
            )}
          </div>
        </Panel>
      )}

      {aptData.description && (
        <Panel className="mt-3">
          <SectionLabel>Description</SectionLabel>
          <p className="text-sm leading-relaxed text-mainText whitespace-pre-line mt-3">
            {aptData.description}
          </p>
        </Panel>
      )}

      {(sectors.length > 0 || countries.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          {sectors.length > 0 && (
            <Panel>
              <SectionLabel>Targeted Sectors</SectionLabel>
              <div className="flex flex-wrap gap-2 mt-3">
                {sectors.map((s) => (
                  <span key={s} className={tagClasses}>
                    {s}
                  </span>
                ))}
              </div>
            </Panel>
          )}
          {countries.length > 0 && (
            <Panel>
              <SectionLabel>
                Observed Countries
                {countries.length > PREVIEW_COUNT && (
                  <span className="ml-2 text-mutedText font-normal">
                    {countries.length} countries
                  </span>
                )}
              </SectionLabel>
              <div className="flex flex-wrap gap-2 mt-3">
                {(countriesExpanded
                  ? countries
                  : countries.slice(0, PREVIEW_COUNT)
                ).map((c) => (
                  <span key={c} className={tagClasses}>
                    {c}
                  </span>
                ))}
                {countries.length > PREVIEW_COUNT && (
                  <ExpandToggle
                    expanded={countriesExpanded}
                    total={countries.length}
                    label="countries"
                    onToggle={() => setCountriesExpanded((v) => !v)}
                  />
                )}
              </div>
            </Panel>
          )}
        </div>
      )}

      {tools.length > 0 && (
        <Panel className="mt-3">
          <SectionLabel>
            Tools & Malware
            {tools.length > PREVIEW_COUNT && (
              <span className="ml-2 text-mutedText font-normal">
                {tools.length} tools
              </span>
            )}
          </SectionLabel>
          <div className="flex flex-wrap gap-2 mt-3">
            {(toolsExpanded ? tools : tools.slice(0, PREVIEW_COUNT)).map(
              (t) => (
                <span key={t} className={toolTagClasses}>
                  {t}
                </span>
              ),
            )}
            {tools.length > PREVIEW_COUNT && (
              <ExpandToggle
                expanded={toolsExpanded}
                total={tools.length}
                label="tools"
                onToggle={() => setToolsExpanded((v) => !v)}
              />
            )}
          </div>
        </Panel>
      )}

      {ops.length > 0 && (
        <Panel className="mt-3">
          <SectionLabel>
            Operations
            <span className="ml-2 text-mutedText font-normal">
              {ops.length} recorded
            </span>
          </SectionLabel>
          <div className="mt-4 flex flex-col gap-1">
            {!opsExpanded && ops.length > OPS_PREVIEW && (
              <button
                onClick={() => setOpsExpanded(true)}
                className={expandBtnClasses}
              >
                Show all {ops.length} operations
              </button>
            )}
            {visibleOps.map((op, i) => (
              <TimelineRow
                key={`${op.date}-${i}`}
                date={op.date}
                activity={op.activity}
              />
            ))}
            {opsExpanded && ops.length > OPS_PREVIEW && (
              <button
                onClick={() => setOpsExpanded(false)}
                className={expandBtnClasses}
              >
                Show recent only
              </button>
            )}
          </div>
        </Panel>
      )}

      {counterOps.length > 0 && (
        <Panel className="mt-3">
          <SectionLabel>
            Counter-Operations
            <span className="ml-2 text-mutedText font-normal">
              {counterOps.length} recorded
            </span>
          </SectionLabel>
          <div className="mt-4 flex flex-col gap-1">
            {!counterOpsExpanded && counterOps.length > OPS_PREVIEW && (
              <button
                onClick={() => setCounterOpsExpanded(true)}
                className={expandBtnClasses}
              >
                Show all {counterOps.length} counter-operations
              </button>
            )}
            {visibleCounterOps.map((op, i) => (
              <TimelineRow
                key={`${op.date}-${i}`}
                date={op.date}
                activity={op.activity}
                isCounter
              />
            ))}
            {counterOpsExpanded && counterOps.length > OPS_PREVIEW && (
              <button
                onClick={() => setCounterOpsExpanded(false)}
                className={expandBtnClasses}
              >
                Show recent only
              </button>
            )}
          </div>
        </Panel>
      )}

      {posts && posts.length > 0 && (
        <Panel className="mt-3">
          <SectionLabel>
            Related Posts
            <span className="ml-2 text-mutedText font-normal">
              {posts.length} posts
            </span>
          </SectionLabel>
          <PostsCarousel posts={posts} />
        </Panel>
      )}
    </>
  );
};

const PostsCarousel = ({ posts }) => {
  const scrollRef = useRef(null);

  const sorted = [...posts].sort((a, b) => {
    const da = a.date?.$date ? new Date(a.date.$date) : 0;
    const db = b.date?.$date ? new Date(b.date.$date) : 0;
    return db - da;
  });

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.6;
    scrollRef.current.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <div className="relative mt-4 -mx-6">
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto px-6 pb-2 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {sorted.map((post, i) => (
          <PostCard
            key={post.document_id || i}
            post={post}
            className="shrink-0 w-44"
          />
        ))}
      </div>
      {sorted.length > 2 && (
        <>
          <button
            onClick={() => scroll(-1)}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-lg flex items-center justify-center border border-btnBorder bg-mainButtonBg text-mutedText hover:text-velBlue transition cursor-pointer"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 3L5 8l5 5" />
            </svg>
          </button>
          <button
            onClick={() => scroll(1)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-lg flex items-center justify-center border border-btnBorder bg-mainButtonBg text-mutedText hover:text-velBlue transition cursor-pointer"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 3l5 5-5 5" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

const PostCard = ({ post, className = "" }) => {
  const [copied, setCopied] = useState(false);

  const postDate = post.date?.$date
    ? new Date(post.date.$date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  const url = post.url;
  const screenshot = post.screenshot;

  const handleClick = () => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleClick}
      disabled={!url}
      className={`group text-left rounded-lg overflow-hidden transition border border-surfaceBorder bg-cardInnerBg hover:border-velBlue/30 disabled:cursor-default ${className}`}
    >
      {screenshot && (
        <div
          style={{ aspectRatio: "1 / 2" }}
          className="w-full overflow-hidden"
        >
          <img
            src={screenshot}
            alt=""
            className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 transition-opacity duration-200"
          />
        </div>
      )}
      <div className="px-2.5 py-2.5">
        <div className="flex items-start justify-between gap-1.5">
          <p className="text-xs tracking-wide text-mainText leading-snug line-clamp-2 flex-1">
            {post.title || "Untitled"}
          </p>
          {url && <CopyIndicator copied={copied} />}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          {post.source && (
            <span className="text-[10px] tracking-wide text-mutedText truncate">
              {post.source}
            </span>
          )}
          {postDate && (
            <span className="text-[10px] tracking-wide text-mutedText tabular-nums shrink-0">
              {postDate}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <h2 className="text-xs tracking-widest uppercase text-mutedText flex items-baseline flex-wrap gap-x-1">
    {children}
  </h2>
);

const MetaItem = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[10px] tracking-widest uppercase text-mutedText">
      {label}
    </div>
    <div className="text-sm tracking-wide text-mainText mt-0.5">{value}</div>
  </div>
);

const AliasTag = ({ name, giver }) => {
  const hasGiver = giver && giver !== "?";
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-cardInnerBg border border-surfaceBorder">
      <span className="tracking-wide text-mainText">{name}</span>
      {hasGiver && (
        <span className="font-normal tracking-wide text-velBlue text-[10px] uppercase">
          {giver}
        </span>
      )}
    </span>
  );
};

const ExpandToggle = ({ expanded, total, label, onToggle }) => (
  <button
    onClick={onToggle}
    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs tracking-wide text-velBlue border border-velBlue/20 hover:bg-velBlue/[0.06] transition cursor-pointer"
  >
    {expanded ? "Show less" : `+${total - PREVIEW_COUNT} more ${label}`}
  </button>
);

const CopyIndicator = ({ copied }) =>
  copied ? (
    <svg
      className="h-3.5 w-3.5 shrink-0 mt-1.5 text-successDot transition-colors duration-200"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3.5 8.5 6.5 11.5 12.5 5.5" />
    </svg>
  ) : (
    <svg
      className="h-3.5 w-3.5 shrink-0 mt-1.5 text-mutedText opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5.5" y="5.5" width="7" height="8" rx="1" />
      <path d="M3.5 10.5v-7a1 1 0 0 1 1-1h5" />
    </svg>
  );

const TimelineRow = ({ date, activity, isCounter = false }) => {
  const [copied, setCopied] = useState(false);
  if (!activity) return null;

  const lines = activity.split("\n");
  const title = lines[0] || "";
  const url = lines.find((l) => l.startsWith("http")) || null;

  const handleClick = () => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleClick}
      disabled={!url}
      className="group flex items-start gap-3 px-3 py-2.5 rounded-lg transition text-left w-full hover:bg-tableRowHover disabled:cursor-default cursor-pointer"
    >
      <div className="shrink-0 pt-1.5">
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${isCounter ? "bg-successDot" : "bg-velBlue"}`}
          style={{
            boxShadow: isCounter
              ? "0 0 6px rgba(34,197,94,0.5)"
              : "0 0 6px rgba(14,165,233,0.5)",
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="text-xs tracking-wide text-mutedText shrink-0 tabular-nums">
            {date}
          </span>
          <span className="text-sm tracking-wide text-mainText leading-snug">
            {title}
          </span>
        </div>
      </div>
      {url && <CopyIndicator copied={copied} />}
    </button>
  );
};

/* ─── Skeleton ─── */
const SkeletonBar = ({ className = "" }) => (
  <div className={`rounded-lg bg-mainButtonBg animate-pulse ${className}`} />
);

const SkeletonCard = ({ hasScreenshots = false }) => (
  <div className="relative rounded-lg overflow-hidden flex flex-col border border-surfaceBorder bg-surface">
    <div className="flex flex-col flex-1 px-4 pt-3.5 pb-3">
      <div className="flex gap-3">
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <SkeletonBar className="w-[26px] h-[20px] shrink-0" />
            <div className="flex-1 min-w-0">
              <SkeletonBar className="h-[20px] w-2/3" />
              <SkeletonBar className="h-[16px] w-1/2 mt-0.5" />
            </div>
          </div>
          <SkeletonBar className="h-[20px] w-3/4 mt-2" />
          <div className="min-h-[24px] flex items-center gap-1.5 my-1.5">
            <SkeletonBar className="h-[24px] w-16 rounded-lg" />
            <SkeletonBar className="h-[24px] w-20 rounded-lg" />
          </div>
        </div>
        {hasScreenshots && (
          <div className="flex items-end justify-end gap-1.5 ml-auto shrink-0 self-stretch pb-1.5">
            <SkeletonBar className="w-[48px] aspect-[1/2] rounded-lg" />
            <SkeletonBar className="w-[48px] aspect-[1/2] rounded-lg" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-auto pt-3.5 border-t border-mainDivider">
        <SkeletonBar className="h-[20px] w-14" />
        <SkeletonBar className="h-[22px] w-20 ml-auto rounded-lg" />
      </div>
    </div>
  </div>
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
    <SkeletonCard hasScreenshots />
    <SkeletonCard hasScreenshots />
    <SkeletonCard />
    <SkeletonCard hasScreenshots />
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard hasScreenshots />
    <SkeletonCard />
    <SkeletonCard />
    <SkeletonCard />
  </div>
);

/* ─── Recency chip ─── */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatLastSeen = (dateStr) => {
  if (!dateStr) return null;
  let date;
  if (dateStr.includes("T") || dateStr.includes("-")) {
    date = new Date(dateStr);
  } else {
    const parts = dateStr.split("-");
    const year = parseInt(parts[0], 10);
    const month = parts[1] ? parseInt(parts[1], 10) - 1 : 0;
    date = new Date(year, month);
  }
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const totalMonthsAgo =
    (now.getFullYear() - date.getFullYear()) * 12 +
    (now.getMonth() - date.getMonth());
  let relative;
  if (totalMonthsAgo <= 0) relative = "This month";
  else if (totalMonthsAgo < 12) relative = `${totalMonthsAgo} mo ago`;
  else {
    const years = Math.floor(totalMonthsAgo / 12);
    relative = `${years} yr${years > 1 ? "s" : ""} ago`;
  }
  const readable = `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  return { relative, readable, totalMonthsAgo };
};

const getRecencyTier = (monthsAgo) => {
  if (monthsAgo <= 6) return "hot";
  if (monthsAgo <= 24) return "warm";
  return "cold";
};

const recencyStyles = {
  hot: {
    dot: "bg-inactiveCase",
    bg: "bg-inactiveCase/10",
    border: "border-inactiveCase/20",
    text: "text-inactiveCase",
  },
  warm: {
    dot: "bg-sunColor",
    bg: "bg-sunColor/10",
    border: "border-sunColor/20",
    text: "text-sunColor",
  },
  cold: {
    dot: "bg-mutedText",
    bg: "bg-searchButtonBg",
    border: "border-surfaceBorder",
    text: "text-mutedText",
  },
};

const LastSeenChip = ({ dateStr }) => {
  const parsed = formatLastSeen(dateStr);
  if (!parsed) return null;
  const tier = getRecencyTier(parsed.totalMonthsAgo);
  const s = recencyStyles[tier];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg border text-[11px] tracking-wide ${s.bg} ${s.border} ${s.text}`}
      title={`Last activity: ${parsed.readable}`}
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`}
      />
      {parsed.relative}
    </span>
  );
};

const UnknownOriginIcon = () => (
  <span className="inline-flex items-center justify-center w-[26px] h-[20px] rounded-lg shrink-0 bg-mainButtonBg text-[11px] font-normal leading-none text-mutedText">
    ?
  </span>
);

const RecentScreenshots = ({ posts }) => {
  if (!posts || posts.length === 0) return null;
  const sorted = [...posts]
    .filter((p) => p.screenshot)
    .sort((a, b) => {
      const dateA = a.date?.$date || a.date || "";
      const dateB = b.date?.$date || b.date || "";
      return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
    })
    .slice(0, 3);
  if (sorted.length === 0) return null;
  return (
    <div className="flex items-end justify-end gap-1.5 ml-auto shrink-0 self-stretch pb-1.5">
      {sorted.map((post, i) => (
        <div
          key={post.document_id || i}
          className="w-[48px] aspect-[1/2] rounded-lg overflow-hidden shrink-0 border border-surfaceBorder"
        >
          <img
            src={post.screenshot}
            alt=""
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
      ))}
    </div>
  );
};

const AptCard = ({ apt, onClickTile, onClickName }) => {
  const ops = apt.operations || [];
  const countries = apt.country || [];
  const motivation = (apt.motivation || []).join(", ");
  const sectors = (apt["observed-sectors"] || []).slice(0, 2);
  const opCount = apt.op_count ?? ops.length;
  const displayName = (apt.actor || "").split(",")[0].trim();
  const firstSeen = apt["first-seen"];
  const recentPosts = apt.recent_posts || [];

  const mostRecentPostDate =
    recentPosts.length > 0
      ? recentPosts
          .map((p) => p.date?.$date || p.date || "")
          .filter(Boolean)
          .sort()
          .pop() || null
      : null;
  const lastActivity =
    mostRecentPostDate || (ops.length > 0 ? ops[ops.length - 1].date : null);

  const primaryCountry = countries[0] || null;
  const flag = getCountryFlag(primaryCountry);
  const hasOrigin =
    primaryCountry && primaryCountry.toLowerCase() !== "unknown";
  const originLabel = hasOrigin
    ? countries.length > 1
      ? countries.join(", ")
      : primaryCountry
    : "Unknown origin";
  const subline = firstSeen
    ? `${originLabel} · since ${firstSeen}`
    : originLabel;

  return (
    <div className="group relative text-left rounded-lg overflow-hidden flex flex-col transition border border-surfaceBorder bg-surface hover:border-velBlue/30 hover:bg-cardInnerBg">
      <button
        onClick={onClickTile}
        className="relative z-10 flex flex-col flex-1 px-4 pt-3.5 pb-3 text-left w-full outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 cursor-pointer"
      >
        <div className="flex gap-3">
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-center gap-3">
              {flag ? (
                <span
                  className="text-xl leading-none shrink-0"
                  role="img"
                  aria-label={primaryCountry}
                >
                  {flag}
                </span>
              ) : (
                <UnknownOriginIcon />
              )}
              <div className="min-w-0 flex-1">
                <h3
                  role="link"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickName();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      e.preventDefault();
                      onClickName();
                    }
                  }}
                  className="text-sm font-normal tracking-wide leading-snug truncate cursor-pointer text-mainText hover:text-velBlue hover:underline decoration-velBlue/40 underline-offset-2 transition"
                >
                  {displayName}
                </h3>
                <span className="text-xs tracking-wide text-mutedText truncate block mt-0.5">
                  {subline}
                </span>
              </div>
            </div>

            <p className="text-[13px] tracking-wide text-mutedText mt-2 line-clamp-1 min-h-[20px]">
              {motivation || "—"}
            </p>

            <div className="min-h-[24px] flex items-center gap-1.5 mt-1.5">
              {sectors.length > 0
                ? sectors.map((s) => (
                    <span
                      key={s}
                      className="inline-flex px-2 py-0.5 rounded-lg text-[11px] tracking-wide truncate max-w-[130px] bg-mainButtonBg text-mutedText"
                    >
                      {s}
                    </span>
                  ))
                : null}
            </div>
          </div>

          {recentPosts.length > 0 && <RecentScreenshots posts={recentPosts} />}
        </div>

        <div className="flex items-center gap-4 mt-auto pt-3.5 border-t border-mainDivider">
          {opCount > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[13px]">
              <span className="font-normal tracking-wide text-velBlue tabular-nums">
                {opCount}
              </span>
              <span className="tracking-wide text-mutedText">
                {opCount === 1 ? "op" : "ops"}
              </span>
            </span>
          )}
          <span className="ml-auto">
            <LastSeenChip dateStr={lastActivity} />
          </span>
        </div>
      </button>
    </div>
  );
};

export const MainApts = () => {
  const [searchString, setSearchString] = useState("");
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [postsTarget, setPostsTarget] = useState(null);
  const { apts, page, hasNext, isLoading, nextPage, prevPage } =
    useAptSearch(searchString);
  const scrollRef = useRef(null);

  const scrollToTop = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const handleBack = () => {
    setSelectedTarget(null);
    scrollToTop();
  };
  const handlePostsBack = () => {
    setPostsTarget(null);
    scrollToTop();
  };
  const handlePageChange = (fn) => {
    fn();
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (selectedTarget)
    return <IndividualApt target={selectedTarget} onBack={handleBack} />;

  if (postsTarget) {
    return (
      <AptPostsView
        target={postsTarget.target}
        actorName={postsTarget.actorName}
        onBack={handlePostsBack}
        onViewProfile={() => {
          const t = postsTarget.target;
          setPostsTarget(null);
          setSelectedTarget(t);
        }}
      />
    );
  }

  return (
    <div
      ref={scrollRef}
      className="relative min-h-screen w-full bg-mainBg font-poppins font-normal text-mainText pt-20 pb-28 px-4 md:px-8"
    >
      <div className="max-w-6xl mx-auto flex flex-col">
        {/* Search bar */}
        <div className="sticky top-14 z-20 pt-6 pb-4">
          <div className="relative">
            <div className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedText z-10">
              <svg
                className="h-4 w-4"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="7" cy="7" r="4.5" />
                <path d="M10.5 10.5L14 14" />
              </svg>
            </div>
            <input
              type="text"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              placeholder="Search threat actors..."
              spellCheck={false}
              className="w-full rounded-lg border border-inputBorder bg-transparent pl-10 pr-4 py-3 text-sm tracking-wide text-mainText placeholder:text-mutedText outline-none ring-inputFocus/60 transition focus:ring-2 shadow-login-panel"
            />
          </div>
        </div>

        {isLoading && <SkeletonGrid />}

        {!isLoading && apts && apts.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {apts.map((apt) => (
              <AptCard
                key={apt.document_id || apt.actor}
                apt={apt}
                onClickTile={() =>
                  setPostsTarget({ target: apt.target, actorName: apt.actor })
                }
                onClickName={() => setSelectedTarget(apt.target)}
              />
            ))}
          </div>
        )}

        {!isLoading && apts && apts.length === 0 && (
          <div className="flex items-center justify-center py-32">
            <p className="text-sm tracking-wide text-mutedText">
              No threat actors found
            </p>
          </div>
        )}

        {!isLoading && apts && apts.length > 0 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-2 rounded-full px-3 py-2 border border-surfaceBorder bg-surface shadow-login-panel">
              <IconPill
                onClick={() => handlePageChange(prevPage)}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <ArrowLongLeftIcon className="h-6 w-6 text-mutedText" />
              </IconPill>
              <div className="min-w-[48px] text-center font-normal rounded-lg px-3 py-1.5 border border-btnBorder bg-mainButtonBg text-mainText">
                {page}
              </div>
              <IconPill
                onClick={() => handlePageChange(nextPage)}
                disabled={!hasNext}
                aria-label="Next page"
              >
                <ArrowLongRightIcon className="h-6 w-6 text-mutedText" />
              </IconPill>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
